import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

interface SegmentOption {
    value: number;
    label: string;
    isDefault?: boolean;
}

interface SegmentedControlProps {
    options: SegmentOption[];
    value: number;
    onChange: (value: number) => void;
    disabled?: boolean;
}

const SegmentContainer = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
        display: 'flex',
    },
}));

const Segment = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isDisabled',
})<{ isSelected?: boolean; isDisabled?: boolean }>(({ theme, isSelected, isDisabled }) => ({
    padding: theme.spacing(0.75, 1.5),
    cursor: isDisabled ? 'default' : 'pointer',
    backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
    color: isSelected ? '#ffffff' : theme.palette.text.primary,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(0.5),
    transition: 'all 0.15s ease',
    opacity: isDisabled ? 0.6 : 1,
    minWidth: 48,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    '&:hover': {
        backgroundColor: isDisabled
            ? (isSelected ? theme.palette.primary.main : 'transparent')
            : (isSelected ? theme.palette.primary.dark : theme.palette.grey[100]),
    },
}));

const SegmentedControl: React.FC<SegmentedControlProps> = ({
    options,
    value,
    onChange,
    disabled = false,
}) => {
    return (
        <SegmentContainer>
            {options.map((option) => (
                <Segment
                    key={option.value}
                    isSelected={option.value === value}
                    isDisabled={disabled}
                    onClick={() => !disabled && onChange(option.value)}
                >
                    <Typography 
                        variant="body2" 
                        fontWeight={option.value === value ? 600 : 400}
                        sx={{ color: 'inherit' }}
                    >
                        {option.label}
                    </Typography>
                </Segment>
            ))}
        </SegmentContainer>
    );
};

export default SegmentedControl;
